import React, { useState } from 'react';
import {
  Box, Card, CardContent, TextField, MenuItem, Button, Stack, Alert, Typography
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSnackbar } from 'notistack';
import PageHeader from '../components/PageHeader';
import PermissionGate from '../components/PermissionGate';
import JsonViewer from '../components/JsonViewer';
import api from '../api';
import { useAuth } from '../context/AuthContext';

const ACTIONS = ['GRANT', 'REVOKE', 'UPDATE'];
const SAMPLE = JSON.stringify({ finance: ['invoice:read', 'invoice:approve'] }, null, 2);

const PermissionSyncForm = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [action, setAction] = useState('GRANT');
  const [userId, setUserId] = useState(user?.user_id || '');
  // The product_permissions key uses the canonical Milient org UUID (the token's milient_org_id).
  const [milientOrgId, setMilientOrgId] = useState(user?.milient_org_id || '');
  const [permsText, setPermsText] = useState(SAMPLE);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const needsPerms = action !== 'REVOKE';

  const submit = async () => {
    setError(null); setResult(null);

    let permissions;
    if (needsPerms) {
      try {
        permissions = JSON.parse(permsText);
      } catch {
        setError('Permissions must be valid JSON, e.g. {"finance": ["invoice:read"]}');
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data } = await api.post('/permission-sync', { action, userId, milientOrgId, permissions });
      setResult(data);
      enqueueSnackbar(`Published (messageId ${data.messageId})`, { variant: 'success' });
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        title="Permission Sync"
        subtitle="Publish a product-permission change to the gateway's SQS queue."
      />

      <Card sx={{ maxWidth: 720 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <TextField select label="Action" value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTIONS.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </TextField>
            <TextField label="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
            <TextField label="Milient Org ID (UUID)" value={milientOrgId} onChange={(e) => setMilientOrgId(e.target.value)} />
            {needsPerms && (
              <TextField
                label="Permissions (JSON)"
                value={permsText}
                onChange={(e) => setPermsText(e.target.value)}
                multiline minRows={5}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />
            )}
            <Typography variant="caption" color="text.secondary">
              product_id and timestamp are added server-side from your configuration.
            </Typography>

            {error && <Alert severity="error">{error}</Alert>}

            <Box>
              <Button
                variant="contained" startIcon={<SendIcon />} onClick={submit}
                disabled={submitting || !userId || !milientOrgId}
              >
                Publish
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {result && (
        <Box sx={{ mt: 3, maxWidth: 720 }}>
          <Alert severity="success" sx={{ mb: 2 }}>Message published — id <strong>{result.messageId}</strong></Alert>
          <JsonViewer title="Published message" data={result.message} defaultExpanded />
        </Box>
      )}
    </Box>
  );
};

const PermissionSync = () => (
  <PermissionGate permission="users:manage">
    <PermissionSyncForm />
  </PermissionGate>
);

export default PermissionSync;
